/**
 * 老板键：一串 2~3 个按键的组合，按下就把整个应用收起来；再按一次恢复。
 *
 * ## 按键用什么表示：**虚拟键码**（`KeyboardEvent.keyCode`）
 *
 * 识别实际发生在 **Rust 侧的系统级键盘钩子**上（见 `apps/desktop/src-tauri/src/bosskey.rs`），
 * 因为窗口 `hide()` 之后页面收不到任何键盘事件 —— 只有系统级钩子能让"同一个快捷键
 * 收起 + 恢复"成立。钩子拿到的是 Windows 的虚拟键码（VK），而 `KeyboardEvent.keyCode`
 * 恰好也是 VK，于是两边说同一套话：前端录什么，Rust 就匹配什么。
 *
 * 由此定下几条规则（都有单测）：
 * - **左右修饰键不分**：`keyCode` 本身就把左右 Ctrl/Shift/Alt 归一（都是 17/16/18），
 *   右 Win（92）这里也归一到左 Win（91），与 Rust 的 `normalize_vk` 对齐；
 * - **要求至少一个修饰键**：否则 `F+F` 这种纯字母序列会在打字时被误触发
 *   （用户给的两个例子都带 Ctrl）；
 * - **不在这里判"长按重复"**：钩子那边按 keydown 计数，浏览器的 `event.repeat` 同理
 *   （录制时会跳过重复事件）。
 */

/** 修饰键的虚拟键码 */
export const VK_SHIFT = 16;
export const VK_CTRL = 17;
export const VK_ALT = 18;
export const VK_WIN = 91;

const MODIFIER_TOKENS = new Set([String(VK_SHIFT), String(VK_CTRL), String(VK_ALT), String(VK_WIN)]);

export const BOSS_KEY_MIN = 2;
export const BOSS_KEY_MAX = 3;

export const BOSS_KEY_STORAGE_KEY = 'xiaotianshu:boss-key:v2';

export type BossKey = readonly string[];

/**
 * 一次按键 → 虚拟键码 token；返回 `null` 表示这个键不参与记录。
 *
 * 只保留数字 token（钩子那边也是数字），脏数据因此天然被挡在外面。
 */
export function keyToken(event: { keyCode?: number }): string | null {
  const vk = Number(event.keyCode ?? 0);
  if (!Number.isInteger(vk) || vk <= 0) return null;
  // 右 Win → 左 Win（与 Rust 的 normalize_vk 对齐）
  return String(vk === 92 ? VK_WIN : vk);
}

/** 少量有名字的键；其余按码段推导（A-Z / 0-9 / F1-F12 / 小键盘） */
const NAMED_LABELS: Record<string, string> = {
  '8': '退格', '9': 'Tab', '13': '回车', '16': 'Shift', '17': 'Ctrl', '18': 'Alt',
  '19': 'Pause', '20': 'CapsLock', '27': 'Esc', '32': '空格', '33': 'PgUp', '34': 'PgDn',
  '35': 'End', '36': 'Home', '37': '←', '38': '↑', '39': '→', '40': '↓', '45': 'Ins', '46': 'Del',
  '91': 'Win', '144': 'NumLock', '186': ';', '187': '=', '188': ',', '189': '-', '190': '.',
  '191': '/', '192': '`', '219': '[', '220': '\\', '221': ']', '222': "'",
};

export function keyLabel(token: string): string {
  if (NAMED_LABELS[token]) return NAMED_LABELS[token];
  const vk = Number(token);
  if (!Number.isInteger(vk)) return token;
  if (vk >= 65 && vk <= 90) return String.fromCharCode(vk);      // A-Z
  if (vk >= 48 && vk <= 57) return String.fromCharCode(vk);      // 0-9
  if (vk >= 112 && vk <= 123) return `F${vk - 111}`;             // F1-F12
  if (vk >= 96 && vk <= 105) return `小键盘${vk - 96}`;
  return `VK${vk}`;
}

export function describeBossKey(keys: BossKey): string {
  return keys.map(keyLabel).join(' + ');
}

export function isModifierToken(token: string): boolean {
  return MODIFIER_TOKENS.has(token);
}

/** 校验组合；返回错误文案，`null` 表示合法 */
export function validateBossKey(keys: BossKey): string | null {
  if (keys.length < BOSS_KEY_MIN) return `至少需要 ${BOSS_KEY_MIN} 个按键`;
  if (keys.length > BOSS_KEY_MAX) return `最多 ${BOSS_KEY_MAX} 个按键`;
  if (!keys.every(k => /^\d+$/.test(k))) return '包含无法识别的按键，请重新录制';
  if (!keys.some(isModifierToken)) return '至少要包含一个 Ctrl / Alt / Shift / Win 键（否则打字时会误触发）';
  // ⚠️ 系统关闭快捷键不能当老板键：录成它之后**按 Alt+F4 关不掉应用**，只会被收起
  //（用户实测报过"设置了组合键后连关都关不掉"）。这里直接拦下，而不是提示。
  if (isSystemCloseCombo(keys)) return 'Alt+F4 是系统的关闭快捷键，请换一个组合（否则会关不掉应用）';
  return null;
}

/** 是否是系统的关闭窗口快捷键（Alt+F4，VK_MENU=18 / VK_F4=115） */
export function isSystemCloseCombo(keys: BossKey): boolean {
  return keys.length === 2 && keys[0] === String(VK_ALT) && keys[1] === '115';
}

/**
 * 组合的"会不会太容易误触发"提示（**不阻止保存**，只提醒）。
 *
 * 全是修饰键的组合（双击 Ctrl 这种）用户明确要求支持，但它在日常里有被误触的可能
 * （例如连续两次单独按 Ctrl）。这里给一句实话，让用户自己权衡。
 */
export function bossKeyWarning(keys: BossKey): string | null {
  if (keys.length > 0 && keys.every(isModifierToken)) {
    return '全是修饰键的组合比较容易被误触发（例如连续两次单独按 Ctrl），建议再带一个普通键（如 Ctrl+Shift+B）';
  }
  return null;
}

/** 组合 → 虚拟键码数组（下发给 Rust 的键盘钩子） */
export function toVirtualKeys(keys: BossKey): number[] {
  return keys.map(Number).filter(vk => Number.isInteger(vk) && vk > 0);
}

/**
 * 规范化显示与存储顺序：**修饰键在前**（Ctrl、Shift、Alt、Win 固定次序），普通键保持录制顺序。
 *
 * 为什么需要：匹配由 Rust 负责（见 `bosskey.rs` 的 `Matcher`），它对修饰键**不分先后**；
 * 用户录的是 `Shift + Ctrl + T`、按的时候多半是"先 Ctrl 再 Shift"。界面与存储统一成规范顺序，
 * 免得"看着一样、按着不一样"让人以为没生效。重复的修饰键保留（双击 Ctrl 靠它）。
 */
const MODIFIER_RANK: Record<string, number> = {
  [String(VK_CTRL)]: 0, [String(VK_SHIFT)]: 1, [String(VK_ALT)]: 2, [String(VK_WIN)]: 3,
};

export function canonicalBossKey(keys: BossKey): string[] {
  const mods = keys.filter(isModifierToken).sort((a, b) => (MODIFIER_RANK[a] ?? 9) - (MODIFIER_RANK[b] ?? 9));
  const plain = keys.filter(k => !isModifierToken(k));
  return [...mods, ...plain];
}

// ── 应用内识别 ──────────────────────────────────────────────────────────
//
// 为什么页面里还要再实现一遍匹配：**实测发现"我们的窗口在前台时"系统级低层钩子收不到按键**
// （切到别的应用则完全正常）。所以两条腿一起走：
//   - 别的应用在前台 → Rust 的全局钩子命中；
//   - 我们自己的应用在前台 → 这里的匹配器命中，然后调 `boss-toggle`。
// 两边都有防抖（Rust 侧 `toggle_all` 400ms、这里 500ms），即使同时到达也不会"收起又立刻恢复"。
//
// ⚠️ 这段语义必须与 `apps/desktop/src-tauri/src/bosskey.rs` 的 `Matcher` 一致
// （修饰键不分先后、忽略长按自动重复、命中后有静默期）。改一处要同步另一处。

/** 命中后的静默期（与 Rust 侧同量级） */
export const HIT_DEBOUNCE_MS = 500;

function sameMultiset(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

export interface BossKeyMatcher {
  /** 返回是否**命中**（命中后应阻止这次按键继续传播） */
  keyDown(vk: number): boolean;
  keyUp(vk: number): void;
}

/** 按配置的组合创建匹配器（配置变化时重建） */
export function createBossKeyMatcher(keys: BossKey): BossKeyMatcher {
  const mods = keys.filter(isModifierToken);
  const plain = keys.filter(k => !isModifierToken(k));
  const modCap = Math.max(mods.length, 1);
  const plainCap = Math.max(plain.length, 1);

  let held: string[] = [];
  let run: string[] = [];
  let tail: string[] = [];
  let pressed: string[] = [];
  let lastHit = 0;

  const forget = (): void => { run = []; tail = []; };

  return {
    keyDown(vk: number): boolean {
      const token = String(vk);
      // 还按着 → 长按的自动重复，不算新的一次按下
      if (pressed.includes(token)) return false;
      pressed.push(token);

      if (keys.length === 0) { forget(); return false; }

      let hit = false;
      if (isModifierToken(token)) {
        if (!held.includes(token)) held.push(token);
        run.push(token);
        if (run.length > modCap) run = run.slice(run.length - modCap);
        hit = plain.length === 0 && sameMultiset(run, mods);
      } else {
        run = [];
        tail.push(token);
        if (tail.length > plainCap) tail = tail.slice(tail.length - plainCap);
        hit = plain.length > 0
          && mods.every(m => held.includes(m))
          && tail.length === plain.length
          && tail.every((t, i) => t === plain[i]);
      }

      if (!hit) return false;
      const now = Date.now();
      if (lastHit !== 0 && now - lastHit < HIT_DEBOUNCE_MS) { forget(); return false; }
      lastHit = now;
      forget();
      return true;
    },

    keyUp(vk: number): void {
      const token = String(vk);
      pressed = pressed.filter(k => k !== token);
      held = held.filter(k => k !== token);
    },
  };
}

/** 录制状态：录制组合时不要让老板键顺手把应用收起来 */
let recording = false;
export function setBossKeyRecording(value: boolean): void { recording = value; }
export function isBossKeyRecording(): boolean { return recording; }

// ── 存取 ────────────────────────────────────────────────────────────────

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function safeStorage(): StorageLike | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

/** 清洗从存储读回的值：长度与合法性都要过，否则当没设置 */
function sanitize(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const keys = raw.filter((k): k is string => typeof k === 'string' && k.length > 0);
  if (keys.length < BOSS_KEY_MIN || keys.length > BOSS_KEY_MAX) return null;
  return validateBossKey(keys) === null ? keys : null;
}

export function getBossKey(storage: StorageLike | null = safeStorage()): string[] | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(BOSS_KEY_STORAGE_KEY);
    const parsed = raw ? sanitize(JSON.parse(raw)) : null;
    // 读回时也规范化：早期版本存的是"录制顺序"（可能出现 Shift + Ctrl + T），
    // 显示与下发统一成规范顺序，免得"看着一样、按着不一样"继续误导
    return parsed ? canonicalBossKey(parsed) : null;
  } catch {
    return null;
  }
}

/** 设置页改完通知同步器（同窗口用事件；另一个窗口靠 `storage` 事件） */
export const BOSS_KEY_CHANGED_EVENT = 'xts:boss-key-changed';

export function setBossKey(keys: string[] | null, storage: StorageLike | null = safeStorage()): void {
  // 统一按规范顺序存（修饰键在前）—— 存储、显示、下发三处因此天然一致
  const normalized = keys && keys.length > 0 ? canonicalBossKey(keys) : null;
  if (storage) {
    try {
      if (normalized) storage.setItem(BOSS_KEY_STORAGE_KEY, JSON.stringify(normalized));
      else storage.removeItem(BOSS_KEY_STORAGE_KEY);
    } catch {
      /* 存储不可用时仅本次会话生效 */
    }
  }
  try {
    window.dispatchEvent(new Event(BOSS_KEY_CHANGED_EVENT));
  } catch {
    /* 非浏览器环境（单测）忽略 */
  }
}
